var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { PageLayout } from '../Components/PageLayout';
import { CurrencyInput } from '../Components/CurrencyInput';
import { useApplication } from '../Contexts/ApplicationContext';
import { observer } from 'mobx-react-lite';
import { PhoneStep } from '../../Services/controllers/StepController';
import { useFormik } from 'formik';
import { useAnalytics } from '../Contexts/AnalyticsContext';
import { useCurrencies } from '../Contexts/CurrenciesContext';
import { Information } from '../Components/Information';
import { debounce } from '../../utils/debounce';
export var CalculationPage = observer(function () {
    var _a, _b;
    var _c = useApplication(), orderFacade = _c.orderFacade, _d = _c.orderFacade, exchangeInfo = _d.exchangeInfo, order = _d.order, facadeErrors = _d.errors;
    var currencies = useCurrencies();
    var track = useAnalytics().track;
    var _e = useState({
        to: exchangeInfo.isDefaultAmount,
    }), skeleton = _e[0], setSkeleton = _e[1];
    var isOrderOneTimeLoaded = useRef(false);
    var amounts = exchangeInfo.amounts;
    var estimateErrorEventSent = useRef(false);
    var hasEstimateRateError = facadeErrors.estimate || amounts.isError;
    useEffect(function () {
        if (!estimateErrorEventSent.current && hasEstimateRateError) {
            track('estimateRates_unavailable', {
                fromAmount: amounts.from.amount,
                fromCurrency: amounts.from.currency.a3,
                toAmount: amounts.to.amount,
                toCurrency: amounts.to.currency.a3,
            });
            estimateErrorEventSent.current = true;
            return;
        }
        if (estimateErrorEventSent.current && !hasEstimateRateError) {
            estimateErrorEventSent.current = false;
            return;
        }
    }, [
        amounts.from.amount,
        amounts.from.currency.a3,
        amounts.to.amount,
        amounts.to.currency.a3,
        hasEstimateRateError,
        track,
    ]);
    var getValidationSchema = function () {
        if (order) {
            return;
        }
        var payMin = amounts.from.currency.minimum;
        var payMax = amounts.from.currency.maximum;
        return Yup.object().shape({
            fromAmount: Yup.string()
                .test('pay amount minimum', "Minimum transaction is ".concat(payMin), function (value) {
                return Number(value) >= payMin;
            })
                .test('pay amount maximum', "Maximum transaction is ".concat(payMax), function (value) {
                return Number(value) <= payMax;
            }),
            toAmount: Yup.string().test('greater than zero', 'The amount must be greater than 0', function (value) {
                return skeleton.to || Number(value) > 0;
            }),
        });
    };
    var _f = useFormik({
        initialValues: {
            fromAmount: amounts.from.amount,
            fromCurrency: amounts.from.currency,
            toAmount: amounts.to.amount,
            toCurrency: amounts.to.currency,
            direction: 'from',
        },
        onSubmit: function () {
            orderFacade.setStepsInfo({
                nextStep: PhoneStep,
                stepsHistory: [],
            });
            track('start_page_clicked', undefined, {});
        },
        validationSchema: getValidationSchema(),
        validateOnChange: true,
        validateOnBlur: true,
    }), handleSubmit = _f.handleSubmit, values = _f.values, setFieldValue = _f.setFieldValue, errors = _f.errors, validateForm = _f.validateForm;
    useEffect(function () {
        if (!isOrderOneTimeLoaded.current && !exchangeInfo.isDefaultAmount) {
            setFieldValue('fromAmount', amounts.from.amount);
            setFieldValue('toAmount', amounts.to.amount);
            setFieldValue('fromCurrency', amounts.from.currency);
            setFieldValue('toCurrency', amounts.to.currency);
            setSkeleton({});
            isOrderOneTimeLoaded.current = true;
        }
    }, [
        amounts.from.amount,
        amounts.from.currency,
        amounts.to.amount,
        amounts.to.currency,
        exchangeInfo,
        setFieldValue,
    ]);
    var fromCurrencies = currencies.from;
    var toCurrencies = currencies.to;
    useEffect(function () {
        track('start_page_loaded');
        validateForm(values);
        // we should miss dependencies here
        // eslint-disable-next-line
    }, []);
    useEffect(function () {
        if ((errors.fromAmount || errors.toAmount) &&
            !(skeleton.from || skeleton.to)) {
            var errorText = errors.fromAmount
                ? "".concat(errors.fromAmount, " ").concat(values.fromCurrency.code)
                : "".concat(errors.toAmount, " ").concat(values.toCurrency.code);
            track('error_raised', {
                error: errorText,
            });
        }
    }, [
        errors.fromAmount,
        errors.toAmount,
        skeleton.from,
        skeleton.to,
        track,
        values.fromCurrency,
        values.toCurrency,
    ]);
    var sentStartPageChangedEvent = useCallback(function (field, direction, value) {
        track('start_page_changed', {
            changed_field_name: field,
            changed_field_value: typeof value === 'string' ? value : value.a3,
        }, {
            direction: direction,
        });
    }, [track]);
    var requestNewRates = useCallback(function (request, fieldName, newValue) {
        sentStartPageChangedEvent(fieldName, request.direction, newValue);
        orderFacade
            .changeExchangeInfo(request)
            .then(function (res) {
            if (!(res === null || res === void 0 ? void 0 : res.amounts)) {
                throw new Error('amounts is not defined');
            }
            // Получаем из ответа новую форму
            var form = {
                fromAmount: res.amounts.from.amount,
                toAmount: res.amounts.to.amount,
                fromCurrency: res.amounts.from.currency,
                toCurrency: res.amounts.to.currency,
                direction: request.direction,
            };
            Object.keys(form).forEach(function (key) {
                if (key === fieldName &&
                    (key === 'fromAmount' || key === 'toAmount')) {
                    return;
                }
                setFieldValue(key, form[key]);
            });
            return form;
        })
            .then(function (form) {
            setTimeout(function () { return validateForm(form); }, 0);
        })
            .catch(function () {
            Object.keys(request).forEach(function (key) {
                setFieldValue(key, request[key]);
            });
        })
            .finally(function () {
            setSkeleton({});
        });
    }, [orderFacade, setFieldValue, validateForm, sentStartPageChangedEvent]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    var debounceServerRequest = useCallback(debounce(requestNewRates, 500), [
        requestNewRates,
    ]);
    var handleFieldChange = useCallback(function (value) {
        var _a, _b;
        var fieldName = Object.keys(value)[0];
        var newValue = value[fieldName];
        // Определяем новый direction
        var direction = fieldName === 'toAmount'
            ? 'to'
            : fieldName === 'fromAmount'
                ? 'from'
                : values.direction;
        // Определяем для чего нужен скелетон
        var skeleton = (_a = {}, _a[direction === 'from' ? 'to' : 'from'] = true, _a);
        setFieldValue(fieldName, newValue);
        setSkeleton(skeleton);
        // Подготавливаем данные для отправки на сервер
        var savedValues = __assign(__assign({}, values), (_b = { direction: direction }, _b[fieldName] = newValue, _b));
        var requestMethod = fieldName.includes('Amount')
            ? debounceServerRequest
            : requestNewRates;
        requestMethod(savedValues, fieldName, newValue);
    }, [values, setFieldValue, debounceServerRequest, requestNewRates]);
    return (_jsxs(PageLayout, __assign({ footer: {
            withCaption: true,
            actionButtonLabel: 'Buy now',
            actionButtonDisable: Boolean(errors.fromAmount ||
                errors.toAmount ||
                hasEstimateRateError ||
                skeleton.from ||
                skeleton.to),
            buttonClick: handleSubmit,
            actionBtnError: hasEstimateRateError
                ? 'Temporarily unavailable'
                : undefined,
        }, headerLabel: "Buy ".concat(values.toCurrency.name) }, { children: [_jsx(CurrencyInput, { searchEnabled: true, searchPlaceholder: "Search for a currency", error: !hasEstimateRateError ? errors.fromAmount : '', dataTestIdInput: "from-input", dataTestIdButton: "from-button", amountInputName: "fromAmount", currencyInputName: "fromCurrency", onChange: handleFieldChange, amount: values.fromAmount, currency: values.fromCurrency, label: "You will pay", currencies: fromCurrencies, currencyModalLabel: "You will pay", precision: (_a = amounts.from.currency) === null || _a === void 0 ? void 0 : _a.precision, skeleton: skeleton.from, hideProtocol: true }), _jsx(CurrencyInput, { error: !hasEstimateRateError ? errors.toAmount : '', searchEnabled: true, searchPlaceholder: "Search for a crypto", dataTestIdInput: "to-input", dataTestIdButton: "to-button", amountInputName: "toAmount", currencyInputName: "toCurrency", onChange: handleFieldChange, amount: values.toAmount, currency: values.toCurrency, precision: (_b = amounts.to.currency) === null || _b === void 0 ? void 0 : _b.precision, label: "You will receive", currencies: toCurrencies, currencyModalLabel: "You will receive", skeleton: skeleton.to }), _jsx(Information, {})] })));
});
